import React, { useState } from 'react';
import { 
  InputNumber, 
  Select, 
  Button, 
  Card, 
  Divider, 
  Form, 
  Result, 
  Row, 
  Col,
  Slider,
  Input,
  Switch,
  Collapse,
  Alert,
  Table
} from 'antd';
import { 
  CalculatorOutlined, 
  HomeOutlined,
  EnvironmentOutlined,
  BuildOutlined,
  ApartmentOutlined,
  StarOutlined,
  WarningOutlined
} from '@ant-design/icons';

const { Option } = Select;
const { Panel } = Collapse;
const { TextArea } = Input;

const AvaluoProfesional = () => {
  const [form] = Form.useForm();
  const [valorAvaluo, setValorAvaluo] = useState(null);
  const [mostrarResultado, setMostrarResultado] = useState(false);
  const [detalleCalculo, setDetalleCalculo] = useState([]);
  const [errorCalculo, setErrorCalculo] = useState(null);

  // Factores de cálculo profesionales
  const factores = {
    tipoPropiedad: {
      casa: { base: 1500, factor: 1.0 },
      apartamento: { base: 1800, factor: 1.2 },
      duplex: { base: 1700, factor: 1.1 },
      penthouse: { base: 2200, factor: 1.5 },
      local: { base: 2000, factor: 1.3 },
      oficina: { base: 1900, factor: 1.25 },
      terreno: { base: 800, factor: 0.7 },
      industrial: { base: 1200, factor: 0.9 }
    },
    
    ubicacion: {
      'centro-ciudad': { factor: 1.5, nombre: 'Centro ciudad' },
      'zona-residencial': { factor: 1.3, nombre: 'Zona residencial' },
      'suburbio': { factor: 1.0, nombre: 'Suburbio' },
      'rural': { factor: 0.7, nombre: 'Zona rural' },
      'playa': { factor: 1.8, nombre: 'Zona playa' },
      'comercial': { factor: 1.6, nombre: 'Zona comercial' }
    },
    
    calidadConstruccion: {
      'económica': 0.8,
      'media': 1.0,
      'alta': 1.3,
      'lujo': 1.7
    },
    
    estadoConservacion: {
      'remodelar': 0.6,
      'regular': 0.8,
      'bueno': 1.0,
      'excelente': 1.2,
      'nuevo': 1.3
    },
    
    servicios: {
      'agua': 1.05,
      'luz': 1.05,
      'gas': 1.03,
      'alcantarillado': 1.07,
      'internet': 1.02
    }
  };

  const calcularAvaluo = (values) => {
    try {
      setErrorCalculo(null);
      let detalles = [];
      
      // Validación de valores requeridos
      if(!values.metrosCuadrados || isNaN(values.metrosCuadrados)) {
        throw new Error('Metros cuadrados no válidos');
      }
      
      if(!values.tipoPropiedad || !factores.tipoPropiedad[values.tipoPropiedad]) {
        throw new Error('Tipo de propiedad no válido');
      }
      
      // 1. Valor base por tipo de propiedad
      const tipo = factores.tipoPropiedad[values.tipoPropiedad];
      let valorBase = parseFloat(values.metrosCuadrados) * parseFloat(tipo.base);
      detalles.push({
        concepto: `Valor base (${values.metrosCuadrados}m² × $${tipo.base}/m²)`,
        valor: valorBase
      });
      
      // 2. Ajuste por tipo de propiedad
      valorBase *= parseFloat(tipo.factor);
      detalles.push({
        concepto: `Factor tipo de propiedad (${tipo.factor}x)`,
        valor: valorBase
      });
      
      // 3. Ajuste por ubicación
      const ubicacion = factores.ubicacion[values.ubicacion] || factores.ubicacion['suburbio'];
      let valorUbicacion = valorBase * parseFloat(ubicacion.factor);
      detalles.push({
        concepto: `Factor ubicación (${ubicacion.nombre}: ${ubicacion.factor}x)`,
        valor: valorUbicacion
      });
      
      // 4. Ajuste por antigüedad (depreciación)
      const antiguedad = values.antiguedad || 0;
      const depreciacion = Math.max(0.3, 1 - (antiguedad * 0.008));
      let valorAntiguedad = valorUbicacion * depreciacion;
      detalles.push({
        concepto: `Depreciación por antigüedad (${antiguedad} años: ${depreciacion.toFixed(2)}x)`,
        valor: valorAntiguedad
      });
      
      // 5. Calidad de construcción
      const calidad = factores.calidadConstruccion[values.calidadConstruccion] || 1.0;
      let valorCalidad = valorAntiguedad * parseFloat(calidad);
      detalles.push({
        concepto: `Calidad construcción (${values.calidadConstruccion || 'media'}: ${calidad}x)`,
        valor: valorCalidad
      });
      
      // 6. Estado de conservación
      const estado = factores.estadoConservacion[values.estadoConservacion] || 1.0;
      let valorEstado = valorCalidad * parseFloat(estado);
      detalles.push({
        concepto: `Estado conservación (${values.estadoConservacion || 'bueno'}: ${estado}x)`,
        valor: valorEstado
      });
      
      // 7. Servicios básicos
      let serviciosFactor = 1;
      if(values.servicios && Array.isArray(values.servicios)) {
        serviciosFactor = values.servicios.reduce((acc, curr) => {
          return acc * (factores.servicios[curr] || 1);
        }, 1);
        detalles.push({
          concepto: `Servicios básicos (${values.servicios.join(', ')}: ${serviciosFactor.toFixed(3)}x)`,
          valor: valorEstado * serviciosFactor
        });
      }
      let valorServicios = valorEstado * serviciosFactor;
      
      // 8. Plusvalía por características especiales
      const caracteristicas = values.caracteristicasEspeciales || 0;
      const plusvalia = 1 + (caracteristicas * 0.05);
      let valorPlusvalia = valorServicios * plusvalia;
      detalles.push({
        concepto: `Plusvalía características (${caracteristicas} × 5% = ${plusvalia.toFixed(2)}x)`,
        valor: valorPlusvalia
      });
      
      // 9. Ajuste por oferta/demanda
      const demanda = values.demanda || 0;
      const mercadoFactor = 1 + (demanda / 100);
      let valorMercado = valorPlusvalia * mercadoFactor;
      detalles.push({
        concepto: `Factor oferta/demanda (${demanda}% = ${mercadoFactor.toFixed(2)}x)`,
        valor: valorMercado
      });
      
      // 10. Ajuste por accesibilidad (0-100 puntos)
      const accesibilidad = values.accesibilidad || 50;
      const accesibilidadFactor = 0.9 + (accesibilidad / 100);
      let valorFinal = valorMercado * accesibilidadFactor;
      detalles.push({
        concepto: `Accesibilidad (${accesibilidad}/100 = ${accesibilidadFactor.toFixed(2)}x)`,
        valor: valorFinal
      });
      
      // Validación final del valor
      if(isNaN(valorFinal)) {
        throw new Error('Error en el cálculo del valor final');
      }
      
      setValorAvaluo(parseFloat(valorFinal.toFixed(2)));
      setDetalleCalculo(detalles);
      setMostrarResultado(true);
      
    } catch (error) {
      console.error('Error en el cálculo:', error);
      setErrorCalculo(`Error al calcular el avalúo: ${error.message}`);
      setMostrarResultado(false);
    }
  };

  const resetearCalculadora = () => {
    form.resetFields();
    setValorAvaluo(null);
    setMostrarResultado(false);
    setDetalleCalculo([]);
    setErrorCalculo(null);
  };

  const columns = [
    {
      title: 'Concepto',
      dataIndex: 'concepto',
      key: 'concepto',
    },
    {
      title: 'Valor Parcial',
      dataIndex: 'valor',
      key: 'valor',
      render: (value) => `$${new Intl.NumberFormat('es-ES').format(value.toFixed(2))}`,
      align: 'right'
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <Card 
          title={
            <div className="flex items-center">
              <HomeOutlined className="mr-2 text-blue-600" />
              <span className="text-xl font-semibold">Calculadora Profesional de Avalúos Inmobiliarios</span>
            </div>
          }
          bordered={false}
          className="shadow-xl"
          extra={
            <div className="flex items-center">
              <EnvironmentOutlined className="mr-1 text-green-500" />
              <span className="text-sm text-gray-600">Versión 2.1 - Profesional</span>
            </div>
          }
        >
          {errorCalculo && (
            <Alert
              message="Error"
              description={errorCalculo}
              type="error"
              showIcon
              icon={<WarningOutlined />}
              className="mb-4"
            />
          )}

          <Form
            form={form}
            layout="vertical"
            onFinish={calcularAvaluo}
            initialValues={{
              demanda: 0,
              caracteristicasEspeciales: 0,
              accesibilidad: 50,
              calidadConstruccion: 'media',
              estadoConservacion: 'bueno'
            }}
          >
            <Collapse defaultActiveKey={['1']} ghost>
              {/* Sección 1: Datos Básicos */}
              <Panel header={<span className="font-semibold"><HomeOutlined className="mr-2" />Datos Básicos de la Propiedad</span>} key="1">
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Metros Cuadrados Construidos"
                      name="metrosCuadrados"
                      rules={[{ required: true, message: 'Ingrese los metros cuadrados' }]}
                    >
                      <InputNumber 
                        min={1} 
                        className="w-full" 
                        placeholder="Ej: 120" 
                        step={0.5}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Metros Cuadrados de Terreno"
                      name="metrosTerreno"
                      rules={[{ required: true, message: 'Ingrese los metros de terreno' }]}
                    >
                      <InputNumber 
                        min={1} 
                        className="w-full" 
                        placeholder="Ej: 200" 
                        step={0.5}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Tipo de Propiedad"
                      name="tipoPropiedad"
                      rules={[{ required: true, message: 'Seleccione el tipo' }]}
                    >
                      <Select placeholder="Seleccione tipo" showSearch>
                        {Object.keys(factores.tipoPropiedad).map(tipo => (
                          <Option key={tipo} value={tipo}>
                            {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Ubicación"
                      name="ubicacion"
                      rules={[{ required: true, message: 'Seleccione ubicación' }]}
                    >
                      <Select placeholder="Seleccione ubicación">
                        {Object.keys(factores.ubicacion).map(key => (
                          <Option key={key} value={key}>
                            {factores.ubicacion[key].nombre}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Antigüedad (años)"
                      name="antiguedad"
                      rules={[{ required: true, message: 'Ingrese la antigüedad' }]}
                    >
                      <InputNumber 
                        min={0} 
                        max={200} 
                        className="w-full" 
                        placeholder="Ej: 10" 
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Número de Habitaciones"
                      name="habitaciones"
                    >
                      <InputNumber 
                        min={0} 
                        className="w-full" 
                        placeholder="Ej: 3" 
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Panel>

              {/* Sección 2: Características Físicas */}
              <Panel header={<span className="font-semibold"><BuildOutlined className="mr-2" />Características Físicas</span>} key="2">
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Calidad de Construcción"
                      name="calidadConstruccion"
                      rules={[{ required: true }]}
                    >
                      <Select placeholder="Seleccione calidad">
                        {Object.keys(factores.calidadConstruccion).map(calidad => (
                          <Option key={calidad} value={calidad}>
                            {calidad.charAt(0).toUpperCase() + calidad.slice(1)}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Estado de Conservación"
                      name="estadoConservacion"
                      rules={[{ required: true }]}
                    >
                      <Select placeholder="Seleccione estado">
                        {Object.keys(factores.estadoConservacion).map(estado => (
                          <Option key={estado} value={estado}>
                            {estado.charAt(0).toUpperCase() + estado.slice(1)}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Número de Baños"
                      name="banos"
                    >
                      <InputNumber 
                        min={0} 
                        className="w-full" 
                        placeholder="Ej: 2" 
                        step={0.5}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Número de Plantas/Niveles"
                      name="plantas"
                    >
                      <InputNumber 
                        min={1} 
                        className="w-full" 
                        placeholder="Ej: 2" 
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="Características Especiales (cada una suma 5% al valor)"
                  name="caracteristicasEspeciales"
                >
                  <Slider 
                    min={0}
                    max={10}
                    marks={{
                      0: '0',
                      2: '2',
                      4: '4',
                      6: '6',
                      8: '8',
                      10: '10+'
                    }}
                    tooltip={{ formatter: value => `${value} características` }}
                  />
                </Form.Item>

                <Form.Item
                  label="Descripción de Características Especiales"
                  name="descripcionCaracteristicas"
                >
                  <TextArea 
                    rows={3} 
                    placeholder="Ej: Piscina, jardín amplio, vista al mar, cocina de lujo..." 
                  />
                </Form.Item>
              </Panel>

              {/* Sección 3: Servicios y Entorno */}
              <Panel header={<span className="font-semibold"><ApartmentOutlined className="mr-2" />Servicios y Entorno</span>} key="3">
                <Form.Item
                  label="Servicios Básicos Disponibles"
                  name="servicios"
                >
                  <Select mode="multiple" placeholder="Seleccione servicios disponibles">
                    {Object.keys(factores.servicios).map(servicio => (
                      <Option key={servicio} value={servicio}>
                        {servicio.charAt(0).toUpperCase() + servicio.slice(1)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Demanda en la Zona (ajuste de mercado)"
                      name="demanda"
                    >
                      <Slider 
                        min={-30}
                        max={30}
                        marks={{
                          '-30': '-30%',
                          '-15': '-15%',
                          '0': '0%',
                          '15': '15%',
                          '30': '30%'
                        }}
                        tooltip={{ formatter: value => `${value}% ajuste` }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Accesibilidad (0-100 puntos)"
                      name="accesibilidad"
                    >
                      <Slider 
                        min={0}
                        max={100}
                        marks={{
                          0: '0',
                          25: '25',
                          50: '50',
                          75: '75',
                          100: '100'
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="¿Tiene Estacionamiento?"
                  name="estacionamiento"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>

                <Form.Item
                  label="¿Áreas Comunes?"
                  name="areasComunes"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Panel>
            </Collapse>

            <Divider />

            <div className="flex justify-between">
              <Button onClick={resetearCalculadora} size="large">
                Limpiar Todo
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<CalculatorOutlined />}
                size="large"
                className="bg-blue-600 hover:bg-blue-700"
              >
                Calcular Avalúo Profesional
              </Button>
            </div>
          </Form>

          {mostrarResultado && valorAvaluo !== null && !isNaN(valorAvaluo) && (
            <div className="mt-8">
              <Result
                icon={<StarOutlined className="text-yellow-500" />}
                title="Valoración Profesional de la Propiedad"
                subTitle="Estimación basada en metodología de avalúo integral"
                extra={[
                  <div key="result" className="w-full">
                    <div className="text-center mb-8">
                      <p className="text-5xl font-bold text-blue-700 my-4">
                        ${new Intl.NumberFormat('es-ES').format(valorAvaluo)}
                      </p>
                      <p className="text-lg text-gray-600">
                        Valor estimado de mercado
                      </p>
                    </div>

                    <Divider orientation="left">Detalle del Cálculo</Divider>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <Table 
                        columns={columns} 
                        dataSource={detalleCalculo} 
                        pagination={false}
                        size="small"
                        rowKey={(record, index) => index}
                      />
                    </div>

                    <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-semibold text-blue-800 mb-2">Notas del Avalúo</h4>
                      <ul className="list-disc pl-5 text-gray-700">
                        <li>Este valor es una estimación profesional basada en los datos proporcionados</li>
                        <li>Para un avalúo oficial se requiere inspección física por perito certificado</li>
                        <li>Los valores pueden variar según fluctuaciones del mercado inmobiliario</li>
                        <li>Se recomienda actualizar la valoración cada 6 meses</li>
                      </ul>
                    </div>
                  </div>
                ]}
              />
            </div>
          )}
        </Card>

        <div className="mt-6 text-center text-gray-500 text-xs">
          <p>© {new Date().getFullYear()} Sistema Profesional de Avalúos Inmobiliarios</p>
          <p>Esta herramienta no sustituye un avalúo oficial certificado</p>
        </div>
      </div>
    </div>
  );
};

export default AvaluoProfesional;